import React, { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  CircularProgress,
  Icon,
} from "@mui/material";
import { API_BASE_URL } from "config/api";

const RefundProgressContext = createContext(null);

export const useRefundProgress = () => {
  const context = useContext(RefundProgressContext);
  if (!context) {
    throw new Error("useRefundProgress must be used within a RefundProgressProvider");
  }
  return context;
};

export const RefundProgressProvider = ({ children }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState("idle"); // 'idle' | 'processing' | 'success' | 'error'
  const [resId, setResId] = useState("");
  const [refundId, setRefundId] = useState(null);
  const [amountStr, setAmountStr] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [countdownSeconds, setCountdownSeconds] = useState(60);

  // 60-second digital stopwatch countdown (00:60, 00:59, 00:58 ... 00:01)
  useEffect(() => {
    let timer = null;
    if (isProcessing) {
      const startTime = Date.now();
      setCountdownSeconds(60);
      timer = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        const remaining = Math.max(1, 60 - elapsed);
        setCountdownSeconds(remaining);
      }, 1000);
    } else {
      setCountdownSeconds(60);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isProcessing]);

  // Execute refund asynchronously from anywhere
  const triggerRefund = async (payload, newDbRecord, callbacks = {}) => {
    const rawAmt = Math.abs(Number(payload?.amount || 0));
    if (!payload || rawAmt <= 0) {
      const errorMsg = "Please enter amount";
      const err = new Error(errorMsg);
      setStatus("error");
      setIsProcessing(false);
      setErrorMessage(errorMsg);
      if (callbacks.onError) {
        callbacks.onError(err);
      }
      setTimeout(() => {
        setStatus((curr) => (curr === "error" ? "idle" : curr));
      }, 8000);
      return;
    }

    setIsProcessing(true);
    setStatus("processing");
    setCountdownSeconds(60);
    const targetReservationId = String(payload.reservationId || "").trim();
    setResId(targetReservationId);
    setRefundId(null);
    setAmountStr(
      payload.currency === "PKR"
        ? `PKR ${Math.abs(Number(payload.amount || 0)).toLocaleString()}`
        : `$${Math.abs(Number(payload.amount || 0)).toFixed(2)} USD`
    );
    setErrorMessage("");

    try {
      const endpoint = `${API_BASE_URL || ""}/api/charges/add`;
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({
        success: false,
        error: "Network error processing refund in Hostaway.",
      }));

      if (!response.ok || data.success === false) {
        throw new Error(
          data.error || data.details || data.message || "Failed to process refund in Hostaway."
        );
      }

      // Check for strictly 6-digit provider refund ID
      const rawCandidate = data.newRefundId || data.refund?.sixDigitRefundId || data.refund?.refundInfo?.id || data.refund?.paymentProviderRefundId || data.refund?.paymentProviderChargeId || data.refund?.id || null;
      const rawStr = rawCandidate ? String(rawCandidate).trim() : "";

      if (/^\d{6}$/.test(rawStr)) {
        // Strictly 6-digit provider refund ID (e.g. 539211)
        setStatus("success");
        setIsProcessing(false);
        setRefundId(rawStr);
        setErrorMessage("");
        if (callbacks.onSuccess) {
          callbacks.onSuccess(newDbRecord, { ...data, newRefundId: rawStr });
        }
      } else {
        // Not a valid 6-digit refund ID -> Show error on frontend!
        const errText = `Hostaway refund verification failed for Reservation #${targetReservationId || resId}`;
        console.warn(errText);
        setStatus("error");
        setIsProcessing(false);
        setRefundId(null);
        setErrorMessage(errText);

        if (callbacks.onError) {
          callbacks.onError(new Error(errText));
        }
        return;
      }

      // Auto-hide progress pill after 8 seconds
      setTimeout(() => {
        setStatus((curr) => (curr === "success" ? "idle" : curr));
      }, 8000);
    } catch (err) {
      let msg = err.message || "Failed to process refund in Hostaway.";
      if (msg.toLowerCase().includes("no charge") || msg.toLowerCase().includes("no charges")) {
        msg = "Please try again refund security deposit";
      } else if (msg.toLowerCase().includes("verification failed") || msg.toLowerCase().includes("provider refund id")) {
        msg = `Hostaway refund verification failed for Reservation #${targetReservationId || resId}`;
      }
      console.warn(msg);
      setStatus("error");
      setIsProcessing(false);
      setErrorMessage(msg);

      if (callbacks.onError) {
        callbacks.onError(new Error(msg));
      }

      // Auto-hide error notification after 8 seconds
      setTimeout(() => {
        setStatus((curr) => (curr === "error" ? "idle" : curr));
      }, 8000);
    }
  };

  const showWidget = status !== "idle";

  return (
    <RefundProgressContext.Provider
      value={{
        isProcessing,
        status,
        resId,
        refundId,
        amountStr,
        countdownSeconds,
        errorMessage,
        triggerRefund,
      }}
    >
      {children}

      {/* Global Single-Line Capsule Pill Widget (Top Right) */}
      {showWidget && (
        <Box
          sx={{
            position: "fixed",
            top: 20,
            right: 24,
            zIndex: 999999,
            backgroundColor:
              status === "error"
                ? "#fff1f2"
                : status === "success"
                ? "#f0fdf4"
                : "#ffffff",
            borderRadius: "50px",
            padding: "8px 14px 8px 16px",
            boxShadow:
              "0 10px 25px -4px rgba(15, 23, 42, 0.1), 0 4px 10px -2px rgba(15, 23, 42, 0.05)",
            display: "inline-flex",
            alignItems: "center",
            gap: 1.25,
            border:
              status === "error"
                ? "1px solid #fecdd3"
                : status === "success"
                ? "1px solid #bbf7d0"
                : "1px solid #e2e8f0",
            backdropFilter: "blur(10px)",
            transition: "all 0.25s ease-in-out",
            fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
            maxWidth: { xs: "92vw", sm: "none" },
          }}
        >
          {/* Status Indicator Icon / Spinner */}
          {status === "processing" && (
            <CircularProgress size={18} thickness={5} sx={{ color: "#0284c7", flexShrink: 0 }} />
          )}
          {status === "success" && (
            <Icon sx={{ color: "#16a34a", fontSize: "20px !important", flexShrink: 0 }}>
              check_circle
            </Icon>
          )}
          {status === "error" && (
            <Icon sx={{ color: "#e11d48", fontSize: "20px !important", flexShrink: 0 }}>
              error
            </Icon>
          )}

          {/* Title & Reservation Info in One Line */}
          {status === "error" ? (
            <Typography
              sx={{
                color: "#be123c",
                fontSize: "0.825rem",
                fontWeight: 600,
                whiteSpace: "nowrap",
                lineHeight: 1,
              }}
            >
              {errorMessage || `Hostaway refund verification failed for Reservation #${resId}`}
            </Typography>
          ) : (
            <>
              <Typography
                sx={{
                  color: status === "success" ? "#166534" : "#0f172a",
                  fontSize: "0.825rem",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  lineHeight: 1,
                }}
              >
                {status === "processing" ? "Refund" : "Refund Completed"}
              </Typography>

              <Typography sx={{ color: "#cbd5e1", fontSize: "0.825rem", lineHeight: 1 }}>•</Typography>

              <Typography
                sx={{
                  color: status === "success" ? "#15803d" : "#475569",
                  fontSize: "0.825rem",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  lineHeight: 1,
                  maxWidth: { xs: "220px", sm: "380px" },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {status === "success" && refundId
                  ? `Res #${resId} • Refund ID: #${refundId} (${amountStr})`
                  : `Res #${resId || "—"} (${amountStr})`}
              </Typography>
            </>
          )}

          {/* 30s Digital Stopwatch Badge (00:30 -> 00:29 -> 00:28 ... -> 00:01) */}
          {status === "processing" && (
            <Box
              sx={{
                backgroundColor: "#e0f2fe",
                borderRadius: "20px",
                px: 1.1,
                py: 0.35,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                flexShrink: 0,
              }}
            >
              <Icon sx={{ fontSize: "14px !important", color: "#0284c7" }}>timer</Icon>
              <Typography
                sx={{
                  color: "#0369a1",
                  fontFamily: "'Roboto Mono', monospace, sans-serif",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  lineHeight: 1,
                }}
              >
                {countdownSeconds}
              </Typography>
            </Box>
          )}

          {/* Dismiss Icon */}
          <Icon
            sx={{
              color: "#94a3b8",
              fontSize: "16px !important",
              cursor: "pointer",
              flexShrink: 0,
              ml: 0.25,
              "&:hover": { color: "#334155" },
            }}
            onClick={() => setStatus("idle")}
          >
            close
          </Icon>
        </Box>
      )}
    </RefundProgressContext.Provider>
  );
};

RefundProgressProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default RefundProgressContext;
